import { getSellerSubmissionPublicFieldLabels } from "../../../utils/sellerIntake/sellerIntakeDefaults";
import { SellerFieldError, sellerFieldDescribedBy, sellerFieldInvalid, sellerIntakeFieldFocusHandler } from "./sellerIntakeFormFieldUtils";
import { useSellerIntakeCopy } from "../../../intl/productFormCopy.js";

const labels = getSellerSubmissionPublicFieldLabels();
const YEAR_MIN = 1950;

function RequiredMark() {
  return (
    <span className="sell-car-form__required" aria-hidden="true">
      *
    </span>
  );
}

/**
 * @param {{
 *   input: import('../../../utils/sellerIntake/sellerIntakeTypes').SellerSubmissionInput,
 *   onChange: (patch: Partial<import('../../../utils/sellerIntake/sellerIntakeTypes').SellerSubmissionInput>) => void,
 *   errors?: Record<string, string|null|undefined>,
 *   disabled?: boolean,
 * }} props
 */
export default function SellerVehicleFields({ input, onChange, errors = {}, disabled = false }) {
  const copy = useSellerIntakeCopy();
  return (
    <fieldset className="sell-car-form__section-card" disabled={disabled}>
      <legend className="sell-car-form__section-title">{copy.vehicleSectionTitle}</legend>

      <div className="sell-car-form__grid">
        <div className="sell-car-form__field">
          <label htmlFor="seller-intake-vehicle-make">
            {labels.vehicleMake} <RequiredMark />
          </label>
          <input
            id="seller-intake-vehicle-make"
            name="vehicleMake"
            type="text"
            autoComplete="off"
            enterKeyHint="next"
            value={input.vehicleMake}
            aria-invalid={sellerFieldInvalid("vehicleMake", errors)}
            aria-describedby={sellerFieldDescribedBy("vehicleMake", errors)}
            onChange={(event) => onChange({ vehicleMake: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          />
          <SellerFieldError fieldKey="vehicleMake" message={errors.vehicleMake} />
        </div>

        <div className="sell-car-form__field">
          <label htmlFor="seller-intake-vehicle-model">
            {labels.vehicleModel} <RequiredMark />
          </label>
          <input
            id="seller-intake-vehicle-model"
            name="vehicleModel"
            type="text"
            autoComplete="off"
            enterKeyHint="next"
            value={input.vehicleModel}
            aria-invalid={sellerFieldInvalid("vehicleModel", errors)}
            aria-describedby={sellerFieldDescribedBy("vehicleModel", errors)}
            onChange={(event) => onChange({ vehicleModel: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          />
          <SellerFieldError fieldKey="vehicleModel" message={errors.vehicleModel} />
        </div>

        <div className="sell-car-form__field">
          <label htmlFor="seller-intake-vehicle-year">
            {labels.vehicleYear} <RequiredMark />
          </label>
          <input
            id="seller-intake-vehicle-year"
            name="vehicleYear"
            type="number"
            inputMode="numeric"
            min={YEAR_MIN}
            max={new Date().getFullYear() + 1}
            enterKeyHint="next"
            value={input.vehicleYear ?? ""}
            aria-invalid={sellerFieldInvalid("vehicleYear", errors)}
            aria-describedby={sellerFieldDescribedBy("vehicleYear", errors)}
            onChange={(event) => onChange({ vehicleYear: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          />
          <SellerFieldError fieldKey="vehicleYear" message={errors.vehicleYear} />
        </div>

        <div className="sell-car-form__field">
          <label htmlFor="seller-intake-vehicle-trim">{labels.vehicleTrim}</label>
          <input
            id="seller-intake-vehicle-trim"
            name="vehicleTrim"
            type="text"
            autoComplete="off"
            enterKeyHint="next"
            value={input.vehicleTrim}
            aria-invalid={sellerFieldInvalid("vehicleTrim", errors)}
            aria-describedby={sellerFieldDescribedBy("vehicleTrim", errors)}
            onChange={(event) => onChange({ vehicleTrim: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          />
          <SellerFieldError fieldKey="vehicleTrim" message={errors.vehicleTrim} />
        </div>

        <div className="sell-car-form__field">
          <label htmlFor="seller-intake-vehicle-mileage">
            {labels.vehicleMileageKm} <RequiredMark />
          </label>
          <input
            id="seller-intake-vehicle-mileage"
            name="vehicleMileageKm"
            type="number"
            inputMode="numeric"
            min={0}
            enterKeyHint="next"
            value={input.vehicleMileageKm ?? ""}
            aria-invalid={sellerFieldInvalid("vehicleMileageKm", errors)}
            aria-describedby={sellerFieldDescribedBy("vehicleMileageKm", errors)}
            onChange={(event) => onChange({ vehicleMileageKm: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          />
          <SellerFieldError fieldKey="vehicleMileageKm" message={errors.vehicleMileageKm} />
        </div>

        <div className="sell-car-form__field">
          <label htmlFor="seller-intake-ownership">
            {labels.ownershipStatus} <RequiredMark />
          </label>
          <select
            id="seller-intake-ownership"
            name="ownershipStatus"
            value={input.ownershipStatus}
            aria-invalid={sellerFieldInvalid("ownershipStatus", errors)}
            aria-describedby={sellerFieldDescribedBy("ownershipStatus", errors)}
            onChange={(event) => onChange({ ownershipStatus: event.target.value })}
            onFocus={sellerIntakeFieldFocusHandler}
          >
            {copy.ownershipOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <SellerFieldError fieldKey="ownershipStatus" message={errors.ownershipStatus} />
        </div>
      </div>
    </fieldset>
  );
}
